/**
 * SNES Chat - Gate Screen
 *
 * Magic word gate for accessing the chat interface.
 * User Story 1: Access Gate Entry (Priority: P1)
 */

import { setGatePassed as stateSetGatePassed, isGatePassed } from './state';

// ============================================================================
// Constants
// ============================================================================

const MAGIC_WORD = '2025';

// ============================================================================
// Validation
// ============================================================================

/**
 * Validate the magic word
 * T020: Implement validateMagicWord function
 */
export function validateMagicWord(input: string): boolean {
  const trimmed = input.trim();
  return trimmed === MAGIC_WORD;
}

/**
 * Check if gate is already passed
 * T021: Uses state management
 */
export function checkGateStatus(): boolean {
  return isGatePassed();
}

/**
 * Set gate as passed
 * T022: Uses state management
 */
export function setGatePassed(): void {
  stateSetGatePassed();
}

// ============================================================================
// UI State
// ============================================================================

let errorMessage: string | null = null;

/**
 * Handle form submission
 */
function handleSubmit(event: Event): void {
  event.preventDefault();

  const form = event.target as HTMLFormElement;
  const input = form.querySelector('input') as HTMLInputElement;
  const value = input.value;

  if (!value.trim()) {
    errorMessage = 'Enter the magic word!';
    renderError();
    return;
  }

  if (validateMagicWord(value)) {
    console.log('[Gate] Magic word accepted');
    errorMessage = null;
    setGatePassed();
  } else {
    console.log('[Gate] Wrong magic word');
    errorMessage = 'Wrong! Try again...';
    renderError();
    input.value = '';
    input.focus();
  }
}

/**
 * Render error message
 */
function renderError(): void {
  const errorEl = document.getElementById('gate-error');
  if (errorEl) {
    if (errorMessage) {
      errorEl.textContent = errorMessage;
      errorEl.style.display = 'block';
    } else {
      errorEl.style.display = 'none';
    }
  }
}

// ============================================================================
// Rendering
// ============================================================================

/**
 * Render the gate screen
 * T023-T024: Create renderGateScreen with input handling
 */
export function renderGateScreen(container: HTMLElement): void {
  errorMessage = null;

  container.innerHTML = `
    <div class="screen screen-centered gate-screen">
      <div class="dialog-box gate-dialog">
        <div class="gate-header">
          <h1 class="gate-title">MAINT-TRACKA</h1>
          <p class="gate-subtitle">Aircraft Maintenance Tracker</p>
        </div>

        <div class="gate-content">
          <p class="gate-prompt">
            Enter the magic word to continue...
          </p>

          <form id="gate-form" class="gate-form">
            <input
              type="password"
              id="gate-input"
              class="input gate-input"
              placeholder="Magic word..."
              autocomplete="off"
              autofocus
            />
            <button type="submit" class="btn gate-btn">
              ENTER
            </button>
          </form>

          <div id="gate-error" class="error-message gate-error" style="display: none;">
          </div>
        </div>

        <div class="gate-footer">
          <p class="gate-hint">
            Hint: What year is it?
          </p>
        </div>
      </div>
    </div>
  `;

  // Add gate-specific styles
  addGateStyles();

  // Attach form handler
  const form = document.getElementById('gate-form');
  if (form) {
    form.addEventListener('submit', handleSubmit);
  }

  // Focus input
  const input = document.getElementById('gate-input') as HTMLInputElement;
  if (input) {
    input.focus();
  }
}

/**
 * Add gate-specific CSS
 */
function addGateStyles(): void {
  const styleId = 'gate-styles';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .gate-screen {
      background: linear-gradient(
        180deg,
        var(--snes-dark-blue) 0%,
        var(--snes-black) 100%
      );
    }

    .gate-dialog {
      max-width: 360px;
      width: 90%;
      text-align: center;
    }

    .gate-header {
      margin-bottom: var(--space-lg);
    }

    .gate-title {
      font-size: var(--font-size-lg);
      color: var(--snes-yellow);
      margin-bottom: var(--space-sm);
      text-shadow: 2px 2px 0 var(--snes-black);
    }

    .gate-subtitle {
      font-size: var(--font-size-xs);
      color: var(--snes-light-blue);
    }

    .gate-prompt {
      font-size: var(--font-size-sm);
      margin-bottom: var(--space-md);
    }

    .gate-form {
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
    }

    .gate-input {
      text-align: center;
      font-size: var(--font-size-lg);
      letter-spacing: 4px;
    }

    .gate-btn {
      width: 100%;
    }

    .gate-error {
      margin-top: var(--space-sm);
      animation: shake 0.3s ease-in-out;
    }

    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-5px); }
      75% { transform: translateX(5px); }
    }

    .gate-footer {
      margin-top: var(--space-lg);
      border-top: 2px solid var(--snes-gray);
      padding-top: var(--space-md);
    }

    .gate-hint {
      font-size: var(--font-size-xs);
      color: var(--snes-gray);
    }
  `;

  document.head.appendChild(style);
}
